import DIAL_CODES from "../Data/phoneDialCodes.json";
import { COUNTRY_OPTIONS, getLocalizedCountryOptions } from "./countryOptions";

const NATIVE_LOCALES = [
  "ar",
  "uk",
  "ru",
  "zh",
  "ja",
  "ko",
  "he",
  "fa",
  "tr",
  "de",
  "fr",
  "es",
  "pt",
  "hi",
  "vi",
  "th",
  "el",
  "bg",
  "cs",
  "ro",
  "hu",
  "pl",
  "nl",
  "sv",
  "no",
  "da",
  "fi",
];

export const DEFAULT_PHONE_COUNTRY_CODE = "AE";

export function getNativeCountryLabel(code, englishLabel) {
  if (typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") {
    return "";
  }

  for (const locale of NATIVE_LOCALES) {
    try {
      const name = new Intl.DisplayNames([locale], { type: "region" }).of(code);
      if (name && name !== englishLabel && name !== code) {
        return name;
      }
    } catch {
      // Skip unsupported locale.
    }
  }

  return "";
}

export function formatPhoneCountryLabel(option) {
  const nativeLabel = getNativeCountryLabel(option.code, option.label);
  return nativeLabel ? `${option.label} (${nativeLabel})` : option.label;
}

export const PHONE_COUNTRY_OPTIONS = COUNTRY_OPTIONS.filter((country) => DIAL_CODES[country.value])
  .map((country) => ({
    code: country.value,
    label: country.label,
    dialCode: DIAL_CODES[country.value],
    flagUrl: `https://flagcdn.com/w40/${country.value.toLowerCase()}.png`,
    searchLabel: `${country.label} ${country.value} +${DIAL_CODES[country.value]}`.toLowerCase(),
  }))
  .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

export function getPhoneCountryOption(code = DEFAULT_PHONE_COUNTRY_CODE, options = PHONE_COUNTRY_OPTIONS) {
  return (
    options.find((option) => option.code === code) ||
    options.find((option) => option.code === DEFAULT_PHONE_COUNTRY_CODE) ||
    options[0]
  );
}

const MIN_NATIONAL_DIGITS = 6;

function uniqueDialCodes(options = []) {
  return [...new Set(options.map((option) => String(option.dialCode || "").replace(/\D/g, "")))]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
}

export function sanitizeNationalPhone(raw, selectedDialCode, options = PHONE_COUNTRY_OPTIONS) {
  const original = String(raw || "");
  const compact = original.replace(/[\s()-]/g, "");
  const hadIntlPrefix = compact.startsWith("+") || compact.startsWith("00");
  let digits = original.replace(/\D/g, "");

  if (digits.startsWith("00")) {
    digits = digits.slice(2);
  }

  const stripDial = (dial) => {
    if (!dial || !digits.startsWith(dial)) return false;
    const rest = digits.slice(dial.length);
    if (rest.length < MIN_NATIONAL_DIGITS) return false;
    digits = rest;
    return true;
  };

  const selected = String(selectedDialCode || "").replace(/\D/g, "");
  if (!stripDial(selected) && hadIntlPrefix) {
    uniqueDialCodes(options).some(stripDial);
  }

  return digits;
}

export function getLocalizedPhoneCountryOptions(locale = "en") {
  const countries = getLocalizedCountryOptions(locale);
  const byCode = new Map(countries.map((country) => [country.value, country]));

  return PHONE_COUNTRY_OPTIONS.map((option) => {
    const localized = byCode.get(option.code);
    const label = localized?.label || option.label;
    return {
      ...option,
      label,
      searchLabel: `${label} ${option.label} ${option.code} +${option.dialCode}`.toLowerCase(),
    };
  }).sort((a, b) => a.label.localeCompare(b.label, locale, { sensitivity: "base" }));
}
